import { Component, inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';

import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';

import { firstValueFrom, Subscription } from 'rxjs';
import { settledCache } from '../../shared/utils/cache-ready';
import { PartySheetsService } from '../../service/google-sheets/party-sheets.service';
import { CommonSheetsService } from '../../service/google-sheets/common-sheets.service';
import { PartyService } from '../../service/entity-services/party.service';
import { UsersService } from '../../service/entity-services/users.service';
import { Party } from '../../objects/party';

import { PartyLabelPipe } from '../../shared/pipes/party-label.pipe';
@Component({
  selector: 'app-new-party-dialog',
  templateUrl: './new-party-dialog.component.html',
  styleUrls: ['./new-party-dialog.component.css'],
  standalone: true,
  imports: [
    PartyLabelPipe,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatInputModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatIconModule,
    MatCardModule,
    MatButtonModule
  ]
})
export class NewPartyDialogComponent implements OnInit, OnDestroy {
  private dialogRef = inject(MatDialogRef<NewPartyDialogComponent>);

  /**
   * Optional context from whoever opened us.
   *
   * `optional: true` matters: the Add New Party button on the contract form opens
   * this dialog with no `data` at all, and a non-optional inject would throw
   * there. The AI intake flow DOES pass a name — the party it extracted from the
   * PDF and failed to match — and until now we dropped it on the floor and made
   * the user retype a name that was sitting right there on screen.
   */
  private dialogData = inject<{ prefilledName?: string } | null>(MAT_DIALOG_DATA, { optional: true });
  private partySheetsService = inject(PartySheetsService);
  private commonSheetsService = inject(CommonSheetsService);
  private partyService = inject(PartyService);
  private usersService = inject(UsersService);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);

  form: FormGroup;
  private formSubscription!: Subscription;

  currentParty!: Party;

  specialties: any[] = [];
  partyTypes: any[] = [];
  partyStanding: any[] = [];
  geographicStates: any[] = [];

  saving = false;
  loading = true;

  // Users for autocomplete
  allUsers$: any;
  filteredUsers: any[] = [];

  /**
   * The COMPLETE user list, kept alongside `filteredUsers` (which the typeahead
   * narrows as the user types). This component does not extend CreateEntity, so it
   * can't use the shared `filterUsers` helper and filters its own copy.
   */
  private allUsersList: any[] = [];
  allParties$: any;

  constructor() {
    const datetimestamp = (new Date).getTime().toString();

    this.allUsers$ = this.usersService.allUsers$;
    this.allParties$ = this.partyService.allParties$;

    // Initialize the form with all controls
    this.form = this.fb.group({
      owner: new FormControl(''),
      vendorNum: [datetimestamp],
      name: [''],
      accountingCompanyName: [''],
      dba: [''],
      parentVendor: [''],
      formallyKnownAs: [''],
      standing: [''],
      partyType: [''],
      website: [''],
      dunsNum: [''],
      npiNum: [''],
      ccnNum: [''],
      isSubContractor: [''],
      isChildOfParentParty: [''],
      address: [''],
      city: [''],
      state: [''],
      zip: [''],
      secondaryAddress: [''],
      secondaryCity: [''],
      secondaryState: [''],
      secondaryZip: [''],
      assessor: [''],
      finalReviewer: [''],
      phonePrimary: [''],
      phoneSecondary: [''],
      annualRevenues: [''],
      numEmployees: [''],
      stateOfIncorporation: [''],
      publicOrPrivate: [''],
      county: [''],
      country: [''],
      nonOrForProfit: [''],
      taxExemptNum: [''],
      tin: [''],
      specialty: [''],
      description: [''],
      baaRequired: [''],
      coiRequired: [''],
    });

    const prefilledName = this.dialogData?.prefilledName?.trim();
    if (prefilledName) {
      this.form.patchValue({ name: prefilledName });
    }
  }

  async ngOnInit() {
    this.loading = true;

    try {
      // Load static field values
      await this.getStaticFieldValues();

      // Load autocomplete data. settledCache, not firstValueFrom — allUsers$ starts
      // as [] and fills asynchronously, so the first emission can be empty and the
      // user pickers would stay blank for the life of the dialog.
      this.allUsersList = await settledCache(this.allUsers$);
      this.filteredUsers = this.allUsersList;

      // Initialize currentParty
      this.currentParty = {
        vendorNum: (new Date).getTime().toString()
      };

      // Subscribe to form changes
      this.formSubscription = this.form.valueChanges.subscribe(changes => {
        this.currentParty = { ...this.currentParty, ...changes };
      });
    } catch (error) {
      console.error('Error during initialization:', error);
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  async getStaticFieldValues() {
    this.partyTypes = await firstValueFrom(this.partySheetsService.getPartyTypes());
    this.partyStanding = await firstValueFrom(this.partySheetsService.getPartyStanding());
    this.geographicStates = await firstValueFrom(this.commonSheetsService.getGeographicStates());
    this.specialties = await firstValueFrom(this.partySheetsService.getPartySpecialties());
  }

  displayUserFn(user: any): string {
    return user && user.shortFriendlyName ? user.shortFriendlyName : '';
  }

  /**
   * Narrow the Owner / Assessor / Final Reviewer pickers as the user types.
   * Matches on `friendlyName` ("First Last (email)") so a first name, last name,
   * or email all find the person.
   */
  filterUsers(event: any): void {
    const query = (event?.target?.value ?? '').toLowerCase();
    this.filteredUsers = query
      ? this.allUsersList.filter(u => (u?.friendlyName ?? '').toLowerCase().includes(query))
      : [...this.allUsersList];
  }

  parentVendorChanged(event: any) {
    console.log('*** parentVendorChanged ***', event);
  }

  cancel() {
    this.dialogRef.close(null);
  }

  async onSubmit() {
    this.saving = true;

    const partyToSave: Party = this.form.value;

    console.log('PARTY TO SAVE: ', partyToSave);

    this.partyService.writeParty(partyToSave).subscribe({
      next: (response) => {
        console.log('Party saved successfully!', response);
        this.saving = false;
        // Close dialog and return the saved party
        this.dialogRef.close(partyToSave);
      },
      error: (err) => {
        console.error('An error occurred:', err);
        this.saving = false;
        alert('Failed to save party. Please try again.');
      },
      complete: () => {
        console.log('writeParty Observable completed');
      }
    });
  }
}
